import { ReactElement, useContext, useId, useMemo } from "react";
import MoreHoriz from "@servicetitan/hammer-icon/mdi/round/more_horiz.svg";
import MoreVert from "@servicetitan/hammer-icon/mdi/round//more_vert.svg";
import { ToolbarItemProps } from "../types";
import { ToolbarButton, ToolbarButtonProps } from "../ToolbarButton";
import {
  ToolbarButtonToggle,
  ToolbarButtonToggleProps,
} from "../ToolbarButtonToggle";
import {
  ToolbarButtonLink,
  ToolbarButtonLinkProps,
} from "../ToolbarButtonLink";
import { ToolbarSelect, ToolbarSelectProps } from "../ToolbarSelect";
import { ToolbarContext, useToolbarSize } from "./ToolbarContext";
import Popover from "../../../internal/components/Popover";
import Icon from "../../Icon";
import { OverflowItemContext } from "./ToolbarItemOverflowContext";
import { handleMenuKeyboardNavigation } from "./utils/accessibility";

import cx from "classnames";
import styles from "../Toolbar.module.scss";

/**
 * Component to render appropriate toolbar items in the overflow menu
 * @param itemType - Type of toolbar item to render
 * @param itemProps - Props for the toolbar item
 * @returns ReactElement - The rendered toolbar item
 */
const OverflowItem = ({ itemType, itemProps }: ToolbarItemProps) => {
  // Based on the item type, use the corresponding toolbar component
  switch (itemType) {
    case "button":
      return <ToolbarButton {...(itemProps as ToolbarButtonProps)} />;

    case "buttonToggle":
      return (
        <ToolbarButtonToggle {...(itemProps as ToolbarButtonToggleProps)} />
      );

    case "buttonLink":
      return <ToolbarButtonLink {...(itemProps as ToolbarButtonLinkProps)} />;

    case "select":
      return <ToolbarSelect {...(itemProps as ToolbarSelectProps)} />;

    default:
      return null;
  }
};

/**
 * Props for the ToolbarOverflowMenu component
 */
export type ToolbarOverflowMenuProps = {
  /**
   * Additional items to display in the overflow menu
   */
  additionalItems?: ReactElement[];
  /**
   * Direction of the parent toolbar
   * @default horizontal
   */
  direction?: "vertical" | "horizontal";
};

/**
 * ToolbarOverflowMenu component displays toolbar items that don't fit in the main toolbar.
 *
 * Features:
 * - Automatic rendering when items overflow in collapse mode
 * - Maintains consistent item ordering with the main toolbar
 * - Preserves item state between toolbar and overflow menu
 * - Provides accessible navigation with keyboard and screen readers
 * - Adapts to both horizontal and vertical toolbar layouts
 * - Context-aware rendering for child components
 * - Proper ARIA menu semantics
 * - Keyboard navigation support
 */
export const ToolbarOverflowMenu = ({
  additionalItems,
  direction,
}: ToolbarOverflowMenuProps) => {
  // Gives us the array of items that need to appear
  const { overflowItems, orderedIds } = useContext(ToolbarContext);
  const menuTriggerId = useId();
  const size = useToolbarSize();

  // Sort overflow items to match their original order in the toolbar
  const orderedItems = useMemo(() => {
    const allElements = orderedIds?.map((id) => {
      return overflowItems.find((item) => item.id === id);
    });

    return allElements?.filter((item) => item !== undefined) || [];
  }, [orderedIds, overflowItems]);

  const popoverButtonClassNames = cx(
    styles["toolbar-button-item"],
    styles["toolbar-overflow-trigger"],
  );

  const overflowIcon = direction === "horizontal" ? MoreHoriz : MoreVert;

  return (
    <Popover noPadding>
      <Popover.Button
        size={size}
        appearance="ghost"
        id={menuTriggerId}
        className={popoverButtonClassNames}
        data-anv="toolbar-overflow-trigger"
        aria-label="Additional toolbar options"
      >
        <Icon svg={overflowIcon} size={size === "xsmall" ? "small" : size} />
      </Popover.Button>
      <Popover.Content>
        <OverflowItemContext.Provider value={{ isInOverflowMenu: true }}>
          <ul
            role="menu"
            className={styles["toolbar-overflow-content"]}
            data-anv="toolbar-overflow-menu"
            aria-labelledby={menuTriggerId}
            onKeyDown={handleMenuKeyboardNavigation}
          >
            {orderedItems.map((item) => {
              const { itemType, itemProps, id } = item;
              return (
                <li
                  key={`${itemType}-${id}`}
                  role="menuitem"
                  data-anv="toolbar-overflow-item"
                >
                  <OverflowItem
                    itemType={itemType}
                    itemProps={itemProps}
                    id={id}
                  />
                </li>
              );
            })}

            {additionalItems?.map((item, index) => (
              <li
                key={`additional-item-${index}`}
                role="menuitem"
                data-anv="toolbar-overflow-item"
              >
                {item}
              </li>
            ))}
          </ul>
        </OverflowItemContext.Provider>
      </Popover.Content>
    </Popover>
  );
};
